import { supabase } from '../../../shared/lib/supabaseClient';
import { logActivity } from '../../../shared/services/activityLogService';

// students is readable column by column (password_hash never is), so queries name their columns.
const STUDENT_COLUMNS =
  'id, lrn, firstname, lastname, middlename, grade_level, section, school_year, gender, age, track, strand, username, has_login, approved, created_at';

// PostgREST filter values containing commas, dots or parentheses must be double-quoted.
const quoteFilterValue = (value) => `"${String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;

// `groups` limits the list to those [{ grade_level, section }] pairs (used for teachers, who only see
// the sections they teach). Leave it undefined for no limit; an empty array matches no students.
export async function getStudents({ search = '', gradeLevel = '', section = '', status = '', groups, page = 1, pageSize = 10 } = {}) {
  if (groups && groups.length === 0) return { data: [], count: 0 };

  let query = supabase
    .from('students')
    .select(STUDENT_COLUMNS, { count: 'exact' })
    .order('lastname', { ascending: true });

  if (search) {
    query = query.or(`lrn.ilike.%${search}%,firstname.ilike.%${search}%,lastname.ilike.%${search}%`);
  }
  if (gradeLevel) query = query.eq('grade_level', gradeLevel);
  if (section) query = query.eq('section', section);
  // 'pending' = waiting for super admin approval, 'no_login' = no password set yet,
  // 'has_login' = can sign in, 'must_change' = can sign in but still on the login staff gave them;
  // '' = everyone.
  if (status === 'pending') query = query.eq('approved', false);
  if (status === 'must_change') query = query.eq('has_login', true).eq('approved', true).eq('must_change_login', true);
  if (status === 'no_login') query = query.eq('has_login', false);
  if (status === 'has_login') query = query.eq('has_login', true).eq('approved', true);
  if (groups) {
    query = query.or(
      groups
        .map((g) => `and(grade_level.eq.${quoteFilterValue(g.grade_level)},section.eq.${quoteFilterValue(g.section)})`)
        .join(',')
    );
  }

  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  const { data, error, count } = await query.range(from, to);
  if (error) throw error;
  return { data, count: count ?? 0 };
}

export async function getDistinctSections(gradeLevel = '') {
  let query = supabase.from('students').select('section').not('section', 'is', null);
  if (gradeLevel) query = query.eq('grade_level', gradeLevel);

  const { data, error } = await query;
  if (error) throw error;
  return [...new Set(data.map((s) => s.section).filter(Boolean))].sort();
}

// Every grade level + section pair with at least one student, school-wide (unlike
// toSectionGroups, which is limited to one teacher's assigned subjects). Used where a viewer
// isn't limited to their own sections, e.g. the super-admin attendance summary.
export async function getDistinctSectionGroups() {
  const { data, error } = await supabase
    .from('students')
    .select('grade_level, section')
    .not('section', 'is', null);
  if (error) throw error;

  const groups = new Map();
  data.forEach(({ grade_level, section }) => {
    if (!grade_level || !section) return;
    groups.set(`${grade_level}|${section}`, { grade_level, section });
  });
  return [...groups.values()].sort(
    (a, b) => a.grade_level.localeCompare(b.grade_level) || a.section.localeCompare(b.section)
  );
}

function friendlyError(error) {
  if (error.code === '23505') {
    return new Error(
      error.message?.includes('students_username_key')
        ? 'That username is already taken.'
        : 'A student with this LRN already exists.'
    );
  }
  if (error.code === '23514' && error.message?.includes('students_username_format')) {
    return new Error('Usernames are 3-50 letters, numbers, dots, dashes or underscores.');
  }
  return error;
}

// Hashed in the database (set_student_password); the password itself is never stored or read back.
// Setting one signs the student out everywhere.
async function setStudentPassword(id, password) {
  const { error } = await supabase.rpc('set_student_password', { p_student_id: id, p_password: password });
  if (error) throw error;
}

// Students can't sign themselves up: staff add them here with their username and password. Only a
// super admin's additions are approved straight away; anyone else's wait for one to approve them
// (the database decides, see students_set_approval in schema.sql).
export async function createStudent({ password, ...student }) {
  const { data, error } = await supabase.from('students').insert(student).select(STUDENT_COLUMNS).single();
  if (error) throw friendlyError(error);
  if (password) await setStudentPassword(data.id, password);
  await logActivity('student_created', `Added student ${student.firstname} ${student.lastname}`);
  return data;
}

// Which of these LRNs already belong to a student, as a Set. Chunked to keep the URL short.
export async function getExistingLrns(lrns) {
  const existing = new Set();
  for (let i = 0; i < lrns.length; i += 100) {
    const { data, error } = await supabase.from('students').select('lrn').in('lrn', lrns.slice(i, i + 100));
    if (error) throw error;
    data.forEach((row) => existing.add(row.lrn));
  }
  return existing;
}

// Runs `task` over `items`, at most `limit` at a time.
async function runLimited(items, limit, task) {
  const queue = [...items];
  const worker = async () => {
    while (queue.length) await task(queue.shift());
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
}

// SF1 import (StudentsPage). Each student's username and starting password are their LRN, which they
// can change on their Profile page. Inserted in one batch; if that's refused (e.g. one username is
// taken), they're retried one by one so the rest still go in. Resolves to { imported, failed }, with
// `failed` as [{ student, message }].
export async function importStudents(students) {
  const rows = students.map((s) => ({ ...s, username: s.lrn }));
  const failed = [];
  let inserted = [];

  const { data, error } = await supabase.from('students').insert(rows).select('id, lrn');
  if (!error) {
    inserted = data;
  } else {
    for (const row of rows) {
      const { data: one, error: rowError } = await supabase.from('students').insert(row).select('id, lrn').single();
      if (rowError) failed.push({ student: row, message: friendlyError(rowError).message });
      else inserted.push(one);
    }
  }

  // A student whose password couldn't be set is still added; staff can set it from Edit.
  await runLimited(inserted, 5, async ({ id, lrn }) => {
    try {
      await setStudentPassword(id, lrn);
    } catch (err) {
      const student = rows.find((r) => r.lrn === lrn);
      failed.push({ student, message: `Added, but the password wasn't set: ${err.message}` });
    }
  });

  if (inserted.length > 0) {
    const { grade_level: grade, section } = rows[0];
    await logActivity(
      'students_imported',
      `Imported ${inserted.length} student${inserted.length === 1 ? '' : 's'} from SF1 (Grade ${grade}${section ? ` - ${section}` : ''})`
    );
  }
  return { imported: inserted.length, failed };
}

// `password` is optional: leave it out to keep the current one.
export async function updateStudent(id, { password, ...student }) {
  const { data, error } = await supabase
    .from('students')
    .update(student)
    .eq('id', id)
    .select(STUDENT_COLUMNS)
    .single();
  if (error) throw friendlyError(error);
  if (password) await setStudentPassword(id, password);
  await logActivity(
    'student_updated',
    `Updated student ${student.firstname} ${student.lastname}${password ? ' (password reset)' : ''}`
  );
  return data;
}

export async function deleteStudent(id, name) {
  const { error } = await supabase.from('students').delete().eq('id', id);
  if (error) throw error;
  await logActivity('student_deleted', `Removed student ${name}`);
}

// Super admins only (the database checks). Taking approval back also signs the student out.
export async function setStudentApproval(id, approved, name) {
  const { error } = await supabase.rpc('set_student_approval', { p_student_id: id, p_approved: approved });
  if (error) throw error;
  await logActivity(
    approved ? 'student_approved' : 'student_approval_revoked',
    `${approved ? 'Approved' : 'Revoked approval of'} student ${name}`
  );
}

// `groups` limits the count to those [{ grade_level, section }] pairs, like getStudents (a teacher's
// own sections). Leave it undefined to count every pending student.
export async function getPendingStudentCount(groups) {
  return countStudents((query) => query.eq('approved', false), groups);
}

// Students who can sign in but haven't chosen their own login yet (must_change_login), for the
// notification bell. `groups` works as in getPendingStudentCount.
export async function getMustChangeLoginStudentCount(groups) {
  return countStudents(
    (query) => query.eq('has_login', true).eq('approved', true).eq('must_change_login', true),
    groups
  );
}

async function countStudents(filter, groups) {
  if (groups && groups.length === 0) return 0;

  let query = filter(supabase.from('students').select('id', { count: 'exact', head: true }));
  if (groups) {
    query = query.or(
      groups
        .map((g) => `and(grade_level.eq.${quoteFilterValue(g.grade_level)},section.eq.${quoteFilterValue(g.section)})`)
        .join(',')
    );
  }

  const { count, error } = await query;
  if (error) throw error;
  return count ?? 0;
}
